// @givmo/app-charity — client entry (SPA boot).
//
// Boot sequence (browser-only; this is a static SPA, no SSR):
//   1. read + validate the env subset (src/lib/env.ts, from Vite `import.meta.env`);
//   2. init client telemetry (Sentry + GA4) — day-one, no secrets;
//   3. init the Firebase Auth + App Check singletons from env;
//   4. configure the shared @givmo/api-client (base URL, `ch` prefix, Bearer + App Check providers);
//   5. create the QueryClient + router and render <App />.
//
// Steps 3–4 are the load-bearing integration ported verbatim (in shape) from the admin app.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app';
import { readCharityEnv } from './lib/env';
import { initClientObservability } from './lib/telemetry';
import { initFirebaseRuntime } from './lib/firebase';
import { configureCharityClient, suiteSessionTransport } from './lib/api-client';
import { createQueryClient } from './lib/query-client';
import { createCharityRouter } from './router/router';
import { registerCharityAssistant } from './features/assistant/assistant-mount';

import '@givmo/design-system/styles.css';
import './app.css';

const rootElement = document.getElementById('root');

try {
  if (rootElement === null) {
    throw new Error('Root element #root not found.');
  }

  const env = readCharityEnv();

  // 2. Telemetry (no-ops without a DSN / supported browser).
  initClientObservability(env);

  // 3. Firebase Auth + App Check from env.
  const { auth, appCheck } = initFirebaseRuntime(env);

  // 4. Configure the shared api-client singleton (every sdk.* call + query hook inherits it).
  configureCharityClient({ env, auth, appCheck });

  // 5. Register the console's assistant control into its slot (only where a scope is declared).
  registerCharityAssistant();

  // 6. Render.
  const queryClient = createQueryClient();
  const router = createCharityRouter();

  createRoot(rootElement).render(
    <StrictMode>
      <App
        auth={auth}
        queryClient={queryClient}
        router={router}
        suiteSession={suiteSessionTransport(env, appCheck)}
      />
    </StrictMode>,
  );
} catch {
  if (rootElement !== null) {
    rootElement.replaceChildren();
    const message = document.createElement('main');
    message.setAttribute('role', 'alert');
    message.style.cssText =
      'min-height:100vh;display:grid;place-items:center;padding:24px;font:600 16px system-ui;text-align:center;color:#243044';
    message.textContent =
      'Givmo for Charities couldn’t start. Check the app configuration and reload this page.';
    rootElement.append(message);
  }
}
